import { useCallback, useEffect, useRef, useState } from 'react';
import { customAlphabet } from 'nanoid';
import { listUserSessions, loadSessionMessages, mergeSessions, readSessions, saveSessions } from '../../services/runtime/chat-sessions';

const newSessionId = customAlphabet('0123456789abcdefghijklmnopqrstuvwxyz', 24);

export default function useChatSessions(appId, userId, requestedSessionId, accessKey, ready, pending = false) {
  const [initial] = useState(() => {
    const saved = readSessions(appId, userId);
    return { ...saved, sessionId: requestedSessionId || saved.activeSessionId || newSessionId() };
  });
  const [sessionId, setSessionId] = useState(initial.sessionId);
  const [sessions, setSessions] = useState(initial.sessions);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const cache = useRef(new Map());
  const active = useRef(sessionId);
  const known = useRef(new Set(initial.sessions.map(item => item.sessionId)));
  if (requestedSessionId) known.current.add(requestedSessionId);

  useEffect(() => {
    saveSessions(appId, userId, { activeSessionId: sessionId, sessions });
  }, [appId, userId, sessionId, sessions]);

  // Pull sessions recorded on the server (other devices, cleared storage) into the local list.
  // Best effort: on any failure the local index is all the widget shows, as before.
  useEffect(() => {
    if (!ready || !accessKey) return undefined;
    let cancelled = false;
    listUserSessions(appId, userId, accessKey).then(remote => {
      if (cancelled || !remote) return;
      remote.forEach(item => known.current.add(item.sessionId));
      setSessions(previous => mergeSessions(previous, remote));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [appId, userId, accessKey, ready]);

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    setError('');
    if (cache.current.has(sessionId) || !known.current.has(sessionId)) {
      setMessages(cache.current.get(sessionId) || []);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    loadSessionMessages(appId, sessionId, accessKey).then(rows => {
      if (!cancelled) { setMessages(rows); setLoading(false); }
    }).catch(err => {
      if (!cancelled) { setError(err.message); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [appId, sessionId, accessKey, ready, revision]);

  const selectSession = useCallback(id => {
    if (id === active.current) return;
    if (!loading && !error && !pending) cache.current.set(active.current, messages);
    else cache.current.delete(active.current);
    active.current = id;
    setLoading(true);
    setError('');
    setMessages([]);
    setSessionId(id);
  }, [messages, loading, error, pending]);

  const recordSession = (message, id = sessionId) => {
    known.current.add(id);
    setSessions(previous => {
      const existing = previous.find(item => item.sessionId === id);
      const now = new Date().toISOString();
      return [{ sessionId: id, title: (existing?.title && existing.title !== 'New chat' ? existing.title : '') || String(message || 'New chat').slice(0, 80),
        createdAt: existing?.createdAt || now, updatedAt: now }, ...previous.filter(item => item.sessionId !== id)];
    });
  };

  return { sessionId, sessions, messages, setMessages, loading, error, active,
    isNew: !known.current.has(sessionId), recordSession, selectSession,
    newSession: () => selectSession(newSessionId()),
    retry: () => { setLoading(true); setRevision(value => value + 1); } };
}
